import {
  AtlasMenuContentItem,
  Button,
  ButtonGroup,
  getColumnSortHeadCellProps,
  getRowInteractionRowProps,
  Menu,
  Table,
} from "@resource/atlas";
import { Pagination } from "components/Pagination/Pagination";
import GlobalFilter from "components/Table/GlobalFilter";
import usePagination from "components/Table/hooks/usePagination";
import useQueryParamsState from "components/Table/hooks/useQueryParamState";
import useRowSelect from "components/Table/hooks/useRowSelect";
import NoTableResults from "components/Table/NoTableResults";
import {
  EmailTemplatesPaginated_currentUserPrisma_currentOrganization_emailTemplates as EmailTemplate,
  EmailTemplateStatusEnum,
  OrderByArg,
} from "generated/schemaTypes";
import _ from "lodash";
import { useEffect, useMemo } from "react";
import useAnalytics from "react-hooks/useAnalytics";
import { useEventCallback } from "react-hooks/useEventCallback";
import {
  Column,
  useFilters,
  useFlexLayout,
  useGlobalFilter,
  useResizeColumns,
  useSortBy,
  useTable,
} from "react-table";
import { DefaultEmailTemplatesStatusFilter } from "utils/constants/defaults";

import BulkActionBar from "../BulkActionBar/BulkActionBar";
import TemplateTableNameCell from "../TemplateTables/TemplateTableNameCell";
import UserTimeAgoTableCell from "../UserTimeAgoTableCell";
import useGetEmailTemplatesActionMenuColumn from "./useGetEmailTemplatesActionMenuColumn";

interface InitialVariables {
  limit: number;
  offset: number;
  search: string;
  status: EmailTemplateStatusEnum;
  orderBy: { [x: string]: string }[];
}

interface RefetchIds {
  search: string;
}

interface EmailTemplateTableProps {
  emailTemplates: EmailTemplate[];
  allEmailTemplateIds: string[];
  refetchIds(refetchIds?: RefetchIds): Promise<void>;
  totalEmailTemplatesCount?: number | null;
  loading: boolean;
  initialVariables: InitialVariables;
  onSelectEmailTemplate(emailTemplateId: string): void;
  onCreateEmailTemplate(): void;
  refetchData(): void;
}

function EmailTemplatesTable({
  emailTemplates,
  allEmailTemplateIds,
  refetchIds,
  totalEmailTemplatesCount,
  loading,
  initialVariables,
  onSelectEmailTemplate,
  onCreateEmailTemplate,
  refetchData,
}: EmailTemplateTableProps) {
  const analytics = useAnalytics();

  const getEmailTemplateActionMenuColumn = useGetEmailTemplatesActionMenuColumn(
    {
      setEmailTemplateToDuplicate: onCreateEmailTemplate,
      afterStatusChangeAction: refetchData,
    }
  );

  const columns = useMemo<Column<EmailTemplate>[]>(
    () => [
      {
        Header: "Name",
        id: "name",
        width: 280,
        Cell: TemplateTableNameCell,
        accessor: "name",
      },
      {
        Header: "Created",
        id: "createdAt",
        Cell: UserTimeAgoTableCell,
        accessor: ({
          creator,
          createdAt: date,
        }: EmailTemplate): {
          user?: {
            name: string;
            imageUrl?: string;
          };
          date: string;
        } => ({ user: creator || undefined, date }),
      },
      getEmailTemplateActionMenuColumn(),
      {
        id: "status",
        width: 0,
        hidden: true,
      },
    ],
    [getEmailTemplateActionMenuColumn]
  );

  const data = useMemo(() => emailTemplates, [emailTemplates]);
  const allIds = useMemo(() => allEmailTemplateIds, [allEmailTemplateIds]);
  const totalCount = useMemo(
    () => totalEmailTemplatesCount,
    [totalEmailTemplatesCount]
  );
  const {
    setFilter,
    getTableProps,
    getTableBodyProps,
    toggleAll,
    headerGroups,
    rows,
    setGlobalFilter,
    setPageSize,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: tableState,
    setAllFilters,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      allIds,
      totalCount,
      manualGlobalFilter: true,
      autoResetGlobalFilter: true,
      getRowId: ({ id }) => id,
      initialState: {
        hiddenColumns: ["status"],
        globalFilter: initialVariables.search ?? "",
        filters: [
          {
            id: "status",
            value: initialVariables.status,
          },
        ],
        pageIndex: initialVariables.offset / initialVariables.limit,
        pageSize: initialVariables.limit,
        sortBy: _(initialVariables.orderBy)
          .flatten()
          .map((value) => ({
            id: _(value).keys().head() as string,
            desc: _(value).values().head() === OrderByArg.desc,
          }))
          .value(),
        isLoading: loading,
      },
      autoResetSelectedRows: false,
      pageCount: 1,
      manualPagination: true,
      autoResetPage: false,
      manualSortBy: true,
      autoResetSortBy: false,
      manualFilters: true,
      autoResetFilters: false,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useQueryParamsState,
    useRowSelect,
    useResizeColumns,
    useFlexLayout
  );
  const { globalFilter, filters, pageSize, pageIndex, selectedRowIds } =
    tableState;

  const selectedIds = _(selectedRowIds).pickBy().keys().value();
  const numSelectedRows = selectedIds.length;

  const status =
    _.find(filters, { id: "status" })?.value ?? EmailTemplateStatusEnum.ALL;

  const isFiltered =
    globalFilter !== "" || status !== DefaultEmailTemplatesStatusFilter;
  const clearFilters = () => {
    setGlobalFilter("");
    setAllFilters(() => [
      {
        id: "status",
        value: DefaultEmailTemplatesStatusFilter,
      },
    ]);
  };

  const hasResults = !!totalEmailTemplatesCount;

  useEffect(() => {
    const updateData = async () => {
      if (numSelectedRows) {
        await refetchIds({
          search: globalFilter,
        });
      }
    };
    updateData();
  }, [globalFilter, refetchIds, numSelectedRows]);

  return (
    <div>
      <div className="flex justify-between mt-5 w-full">
        <GlobalFilter
          placeholder="Search by name"
          className="flex-shrink-0"
          globalFilter={globalFilter ?? ""}
          setGlobalFilter={setGlobalFilter}
        />
        <div className="flex justify-end flex-shrink-0">
          <ButtonGroup>
            <Menu.Root>
              <Menu.Trigger>
                <Button isGhost isDropdown>
                  {_.capitalize(status ?? DefaultEmailTemplatesStatusFilter)}
                </Button>
              </Menu.Trigger>
              <Menu.Content
                items={useMemo(
                  () =>
                    [
                      ["All", EmailTemplateStatusEnum.ALL],
                      ["Active", EmailTemplateStatusEnum.ACTIVE],
                      ["Archived", EmailTemplateStatusEnum.ARCHIVED],
                    ].map(
                      ([name, option]): AtlasMenuContentItem => ({
                        _type: "item",
                        children: name,
                        key: option,
                        isSelectable: true,
                        isSelected: status === option,
                        onSelect: () => setFilter("status", option),
                      })
                    ),
                  [setFilter, status]
                )}
              />
            </Menu.Root>
            <Button variant="primary" onClick={() => onCreateEmailTemplate()}>
              New email template
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <Table.Root {...getTableProps()} className="mt-4">
        <Table.Head role="rowgroup">
          {_.map(headerGroups, (headerGroup) => (
            <Table.HeadRow {...headerGroup.getHeaderGroupProps()}>
              {_.map(headerGroup.headers, (column) => (
                <Table.HeadCell
                  {...column.getHeaderProps()}
                  {...getColumnSortHeadCellProps(column)}
                >
                  {column.render("Header")}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body {...getTableBodyProps()}>
          {_.map(rows, (row) => {
            prepareRow(row);
            return (
              <Table.Row
                {...row.getRowProps()}
                {...getRowInteractionRowProps(
                  { state: tableState, row },
                  {
                    onClick: () => {
                      onSelectEmailTemplate(row.original.id);
                    },
                  }
                )}
              >
                {row.cells.map((cell) => (
                  <Table.Cell {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
          {!rows.length && (
            <NoTableResults
              loading={loading}
              hasResults={hasResults}
              isFiltered={isFiltered}
              clearFilters={clearFilters}
            />
          )}
        </Table.Body>
      </Table.Root>
      <BulkActionBar
        isVisible={!!numSelectedRows}
        selectedCount={numSelectedRows}
        totalCount={totalCount ?? 0}
        itemDisplayText="email template"
        isLoading={loading}
        onSelectAll={() => {
          analytics.track(
            "EmailTemplates Table - Select All EmailTempaltes in BulkActionBar Clicked"
          );
          toggleAll(true);
        }}
        onClearAll={() => toggleAll(false)}
        onClose={() => toggleAll(false)}
      >
        <Menu.Root>
          <Menu.Trigger>
            <Button size="small">Bulk Actions</Button>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Item
              // eslint-disable-next-line no-console
              onSelect={useEventCallback(() => console.log("archive clicked"))}
            >
              Archive
            </Menu.Item>
          </Menu.Content>
        </Menu.Root>
      </BulkActionBar>
      {hasResults ? (
        <Pagination
          totalCount={totalEmailTemplatesCount}
          pageSize={pageSize}
          pageIndex={pageIndex}
          isLoading={loading}
          onPrevPage={() => {
            analytics.track("EmailTemplates Table - Page Changed", {
              direction: "previous",
            });
            previousPage();
          }}
          onNextPage={() => {
            analytics.track("EmailTemplates Table - Page Changed", {
              direction: "next",
            });
            nextPage();
          }}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          onPageSizeChange={(newPageSize: number) => {
            analytics.track("EmailTemplates Table - Page Size Changed", {
              target: newPageSize,
              previousValue: pageSize,
            });
            setPageSize(newPageSize);
          }}
        />
      ) : null}
    </div>
  );
}

export default EmailTemplatesTable;
